import { Link } from "react-router-dom"

import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import { Bee } from "~/src/components/BeeKit"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
  InsightsPanelNoDataDefault,
} from "~/src/components/Dashboard/InsightsPanel"
import { WithErrors } from "~/src/types/withErrors"

type ThankYouNotesDataRow = {
  recipientName: string
  message: string
  reaction?: Bee.ReactionType
  orderHref: string
}

export type ThankYouNotesData = WithErrors<{ notes: ThankYouNotesDataRow[] }>

export type StorefrontInsightsThankYouNotesPanelRenderProps = {
  status: Status
  notes?: ThankYouNotesDataRow[]
}

const NUMBER_OF_NOTES_SHOWN = 5

export function StorefrontInsightsThankYouNotesPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<ThankYouNotesData>("thank_you_notes")

  const notes = data?.notes

  let status: Status
  if (isLoading) {
    status = "loading"
  } else if (data?.errors) {
    status = "error"
  } else if (notes && notes.length > 0) {
    status = "success"
  } else {
    status = "noData"
  }

  return <StorefrontInsightsThankYouNotesRenderPanel status={status} notes={notes} />
}

export function StorefrontInsightsThankYouNotesRenderPanel({
  status,
  notes,
}: StorefrontInsightsThankYouNotesPanelRenderProps) {
  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Recipient feedback" />
      <InsightsPanelContent className="overflow-y-auto">
        <ThankYouNotesContent status={status} rows={notes} />
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function ThankYouNotesContent({ status, rows }: { status: Status; rows?: ThankYouNotesDataRow[] }) {
  if (status === "loading") {
    return <ThankYouNotesSkeleton />
  }
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !rows) return <InsightsPanelNoDataDefault />

  return (
    <>
      <ThankYouNotesTable rows={rows} />
      <ThankYouNotesList rows={rows} />
    </>
  )
}

function ThankYouNotesTable({ rows }: { rows?: ThankYouNotesDataRow[] }) {
  return (
    <table className="hidden w-full @sm:block">
      {/* Table Header */}
      <thead className="border-b bg-slate-200 text-xs font-semibold text-slate-700">
        <tr>
          <th className="w-[20%] px-4 py-2 text-left">RECIPIENT</th>
          <th className="w-[50%] px-4 py-2 text-left">MESSAGE</th>
          <th className="w-[10%] px-4 py-2 text-right">REACTION</th>
          <th className="w-[20%] px-4 py-2 text-right">ORDER</th>
        </tr>
      </thead>
      {/* Table Body */}
      <tbody>
        {rows?.map((recipient) => (
          <tr key={recipient.recipientName} className="border-b last:border-b-0">
            <td className="px-4 py-4 align-top">{recipient.recipientName}</td>
            <td className="px-4 py-4 align-top">
              <p className="line-clamp-6">{recipient.message}</p>
            </td>
            <td className="flex w-full justify-end px-4 py-4 align-top">
              {recipient.reaction && <Bee.Reaction reaction={recipient.reaction} className="text-sm" />}
            </td>
            <td className="px-4 py-4 text-right align-top">
              <Link to={recipient.orderHref} className="text-blue-600" target="_blank" rel="noreferrer">
                View
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ThankYouNotesList({ rows }: { rows?: ThankYouNotesDataRow[] }) {
  return (
    <div className="flex flex-col @sm:hidden">
      {rows?.map((recipient) => (
        <div key={recipient.recipientName} className="flex flex-col gap-1 border-b p-4">
          <div className="flex items-start justify-between">
            <p>{recipient.recipientName}</p>
            <Link to={recipient.orderHref} className="text-blue-600" target="_blank" rel="noreferrer">
              View
            </Link>
          </div>
          {recipient.reaction && (
            <div className="mt-1">
              <Bee.Reaction reaction={recipient.reaction} className="text-sm" />
            </div>
          )}
          <p className="text-gray-600">{recipient.message}</p>
        </div>
      ))}
    </div>
  )
}

function ThankYouNotesSkeleton() {
  return (
    <div className="h-full">
      {/* Mobile skeleton */}
      <div className="@sm:hidden">
        {[...Array(NUMBER_OF_NOTES_SHOWN)].map((_, idx) => (
          <div key={idx} className="flex animate-pulse gap-4 border-b p-4">
            <div className="flex w-full flex-col justify-between gap-2">
              <div className="h-3 w-44 rounded-full bg-zinc-200" />
              <div className="h-3 w-24 rounded-full bg-zinc-200" />
              <div className="h-3 w-12 rounded-full bg-zinc-200" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop skeleton */}
      <div className="hidden @sm:block">
        <table className="w-full [&_td]:py-4 [&_th]:py-2">
          <thead className="border-b bg-slate-200 text-xs font-semibold text-slate-700">
            <tr>
              <th className="w-[20%] px-4 py-2 text-left">RECIPIENT</th>
              <th className="w-[50%] px-4 py-2 text-left">MESSAGE</th>
              <th className="w-[10%] px-4 py-2 text-right">REACTION</th>
              <th className="w-[20%] px-4 py-2 text-right">ORDER</th>
            </tr>
          </thead>
          <tbody className="gap-2">
            {[...Array(NUMBER_OF_NOTES_SHOWN)].map((_, idx) => (
              <tr key={idx} className="animate-pulse border-b last:border-b-0">
                <td className="px-4">
                  <div className="h-3 w-16 rounded-full bg-zinc-200" />
                </td>
                <td className="px-4">
                  <div className="h-3 w-full rounded-full bg-zinc-200" />
                </td>
                <td className="px-4">
                  <div className="ml-auto h-3 w-8 rounded-full bg-zinc-200" />
                </td>
                <td className="px-4 text-right">
                  <div className="ml-auto h-3 w-14 rounded-full bg-zinc-200" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
